import DSBox from '../../design-system/DSBox';
import { DSTypography } from '../../design-system/Typography';
import DSTextField from '../../design-system/DSTextField';
import DSIconButton from '../../design-system/DSIconButton';
import DSFormControl from '../../design-system/DSFormControl';
import DSFormControlLabel from '../../design-system/DSFormControlLabel';
import DSRadioGroup from '../../design-system/DSRadioGroup';
import DSRadio from '../../design-system/DSRadio';
import DSChip from '../../design-system/DSChip';
import DSCheckbox from '../../design-system/DSCheckBox';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FileUploadOutlined } from '@mui/icons-material';
import useAdminGiftForm from './hooks/useAdminGiftForm';
import { isMobile } from 'react-device-detect';
import { MAX_GREETING_LENGTH, MAX_SENDER_NAME_LENGTH } from './consts';

// The uploaded file shows as a chip that fills its field. The empty text input
// next to it is hidden (HIDE_EMPTY_INPUT_SX), otherwise it shares the width and
// the chip stays small and cuts the file name early.
const FILE_CHIP_SX = {
  flex: 1,
  minWidth: 0,
  maxWidth: 'none',
  justifyContent: 'space-between',
  mr: 1,
  // Cut the file name before the delete icon, not under it.
  '.MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
const HIDE_EMPTY_INPUT_SX = { '.MuiInputBase-input': { display: 'none' } };

const AdminGiftForm = () => {
  const {
    theme,
    greeting,
    name,
    selectedOption,
    profileUrl,
    pendulumLogoUrl,
    logoFull,
    fileInputRef,
    logoInputRef,
    localCustomKlydoId,
    klydoGiftProps,
    defaultPhoto,
    profileImageOption,
    setProfileImageOption,
    handleProfileClick,
    handleLogoClick,
    handleCustomKlydo,
    handleOptionChange,
    handleLogoFullChange,
    handleGreetChange,
    handleNameChange,
    handleDateChange,
    handleRemoveProfile,
    handleRemoveLogo,
    handlePickProfile,
    handlePickLogo,
    linesLimit,
  } = useAdminGiftForm();

  return (
    <DSBox
      width="100%"
      sx={{
        backgroundColor: theme.palette.background.paper,
        paddingTop: { xs: 2, md: 10 },
        paddingBottom: { xs: 10, md: 0 },
      }}
    >
      <DSBox
        gap={isMobile ? 0.5 : 5}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: isMobile ? 'column' : 'row-reverse',
          alignItems: 'center',
          textAlign: 'left',
        }}
      >
        <DSBox
          width={isMobile ? '80%' : '350px'}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            textAlign: 'left',
          }}
        >
          {/* Custom Klydo ID */}
          <DSBox>
            <DSTypography
              sx={{
                fontSize: '16px',
                color: '#C9C9C9',
                fontFamily: 'Gilroy-SemiBold',
              }}
            >
              Change to custom klydo
            </DSTypography>
            <DSBox py={0.5} />
            <DSTextField
              placeholder="Enter Klydo ID"
              value={localCustomKlydoId}
              onChange={(e) => handleCustomKlydo(e.target.value)}
              fullWidth
              sx={{ backgroundColor: '#212121', color: '#C9C9C9' }}
            />
            <DSBox py={1} />

            {/* Greeting */}
            <DSTypography
              sx={{
                fontSize: '16px',
                color: '#C9C9C9',
                fontFamily: 'Gilroy-SemiBold',
              }}
            >
              Write your greeting
            </DSTypography>
            <DSBox py={0.5} />
            <DSTextField
              placeholder="Dear Ron..."
              value={greeting}
              onChange={(e) => handleGreetChange(e.target.value)}
              multiline
              fullWidth
              rows={5}
              maxRows={8}
              slotProps={{
                input: {
                  inputProps: { maxLength: MAX_GREETING_LENGTH },
                  style: { padding: '20px' },
                  onKeyDown: linesLimit,
                },
              }}
              sx={{
                backgroundColor: '#212121',
                '.MuiInputBase-input': {
                  overflowWrap: 'break-word',
                  fontSize: '15px',
                  maxWidth: '300px',
                  display: 'block',
                },
              }}
            />
            <DSBox py={0.5} />
            <DSTypography
              sx={{
                fontSize: '16px',
                color: '#C9C9C9',
                fontFamily: 'Gilroy-SemiBold',
              }}
            >
              {greeting.length}/{MAX_GREETING_LENGTH}
            </DSTypography>
          </DSBox>

          <DSBox py={1} />

          {/* Date */}
          <DSBox>
            <DSTypography
              sx={{
                fontSize: '16px',
                color: '#C9C9C9',
                fontFamily: 'Gilroy-SemiBold',
              }}
            >
              Date of occasion
            </DSTypography>
            <DSBox py={0.5} />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={!klydoGiftProps?.date ? 'MM/DD/YYYY' : ''}
                value={
                  klydoGiftProps?.date
                    ? dayjs(new Date(klydoGiftProps.date))
                    : null
                }
                onChange={handleDateChange}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '28px',
                    backgroundColor: '#212121',
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiInputBase-input': { paddingLeft: '16px' },
                }}
              />
            </LocalizationProvider>
          </DSBox>

          <DSBox py={3} />

          {/* Identity: name or pendulum logo */}
          <DSTypography
            sx={{
              fontSize: '16px',
              color: '#C9C9C9',
              fontFamily: 'Gilroy-SemiBold',
            }}
          >
            Identity:
          </DSTypography>
          <DSFormControl>
            <DSRadioGroup
              row
              value={selectedOption}
              onChange={handleOptionChange}
              sx={{ color: '#C9C9C9' }}
            >
              <DSFormControlLabel
                value="text"
                control={<DSRadio />}
                label="Name"
                sx={{ '.MuiFormControlLabel-label': { color: '#C9C9C9' } }}
              />
              <DSFormControlLabel
                value="logo"
                control={<DSRadio />}
                label="Pendulum Logo"
                sx={{ '.MuiFormControlLabel-label': { color: '#C9C9C9' } }}
              />
            </DSRadioGroup>
          </DSFormControl>

          {selectedOption === 'text' ? (
            <DSTextField
              placeholder="Enter Name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              fullWidth
              slotProps={{
                input: { inputProps: { maxLength: MAX_SENDER_NAME_LENGTH } },
              }}
              sx={{
                backgroundColor: '#212121',
                '.MuiInputBase-input': { fontSize: '15px' },
              }}
            />
          ) : (
            <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DSBox>
                <DSFormControlLabel
                  control={
                    <DSCheckbox
                      checked={logoFull}
                      onChange={(e) => handleLogoFullChange(e.target.checked)}
                    />
                  }
                  label="Full bob"
                  sx={{ '.MuiFormControlLabel-label': { color: '#C9C9C9' } }}
                />
                <DSTypography
                  sx={{
                    fontSize: '13px',
                    color: '#8C8C8C',
                    fontFamily: 'Gilroy-SemiBold',
                    ml: '31px', // lines up with the label text, past the checkbox
                    lineHeight: 1.4,
                  }}
                >
                  Fills the whole pendulum, no name. Use a square image, at
                  least 410×410px: smaller ones are enlarged, non-square ones
                  are cropped.
                </DSTypography>
              </DSBox>
              {/* Big preview of the pendulum bob, centered above the input. */}
              <DSBox sx={{ display: 'flex', justifyContent: 'center' }}>
                <DSBox
                  sx={{
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: pendulumLogoUrl
                      ? '1px solid #ccc'
                      : '1px dashed #5A5A5A',
                    backgroundColor: '#212121',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {pendulumLogoUrl ? (
                    <img
                      src={pendulumLogoUrl}
                      alt="Preview"
                      style={{
                        width: '100%',
                        height: '100%',
                        // A full logo fills the whole bob; a sized one is an icon.
                        objectFit: logoFull ? 'cover' : 'contain',
                      }}
                    />
                  ) : (
                    <FileUploadOutlined
                      sx={{ color: '#5A5A5A', fontSize: 40 }}
                    />
                  )}
                </DSBox>
              </DSBox>
              <DSTextField
                disabled
                fullWidth
                placeholder={pendulumLogoUrl ? '' : 'Upload a file'}
                sx={pendulumLogoUrl ? HIDE_EMPTY_INPUT_SX : undefined}
                InputProps={{
                  startAdornment: pendulumLogoUrl ? (
                    <DSChip
                      label={pendulumLogoUrl.split('/').pop()}
                      onDelete={handleRemoveLogo}
                      sx={FILE_CHIP_SX}
                    />
                  ) : null,
                  endAdornment: (
                    <DSIconButton
                      disabled={!!pendulumLogoUrl}
                      onClick={handleLogoClick}
                    >
                      <FileUploadOutlined />
                    </DSIconButton>
                  ),
                }}
              />
              <input
                type="file"
                accept=".png"
                ref={logoInputRef}
                onChange={handlePickLogo}
                style={{ display: 'none' }}
              />
            </DSBox>
          )}

          <DSBox py={4} />

          {/* Profile image */}
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 4,
            }}
          >
            <DSBox>
              <DSFormControl>
                <DSRadioGroup
                  row
                  value={profileImageOption}
                  onChange={(e) => setProfileImageOption(e.target.value)}
                  sx={{ color: '#C9C9C9' }}
                >
                  <DSFormControlLabel
                    value="defualtPhoto"
                    control={<DSRadio />}
                    label="Default photo"
                    sx={{ '.MuiFormControlLabel-label': { color: '#C9C9C9' } }}
                  />
                  <DSFormControlLabel
                    value="userPhoto"
                    control={<DSRadio />}
                    label="Your photo"
                    sx={{ '.MuiFormControlLabel-label': { color: '#C9C9C9' } }}
                  />
                </DSRadioGroup>
              </DSFormControl>
              <DSTextField
                disabled
                fullWidth
                placeholder={profileUrl === defaultPhoto ? 'Upload a file' : ''}
                sx={
                  profileUrl !== defaultPhoto ? HIDE_EMPTY_INPUT_SX : undefined
                }
                InputProps={{
                  startAdornment:
                    profileUrl !== defaultPhoto ? (
                      <DSChip
                        label={profileUrl.split('/').pop()}
                        onDelete={handleRemoveProfile}
                        sx={FILE_CHIP_SX}
                      />
                    ) : null,
                  endAdornment: (
                    <DSIconButton
                      disabled={profileUrl !== defaultPhoto}
                      onClick={handleProfileClick}
                    >
                      <FileUploadOutlined />
                    </DSIconButton>
                  ),
                }}
              />
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handlePickProfile}
                style={{ display: 'none' }}
              />
            </DSBox>
            <DSBox sx={{ paddingTop: 2 }}>
              <img
                src={profileUrl}
                alt="Preview"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: 'contain',
                  borderRadius: 50,
                  border: '1px solid #ccc',
                }}
              />
            </DSBox>
          </DSBox>
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default AdminGiftForm;
